// WebAudio utility for consistent SFX/music/voice playback with volume control
// Usage: const player = new WebAudioPlayer(); player.play('/audio/sfx/foo.mp3', { volume: 0.2, loop: false });

export class WebAudioPlayer {
  constructor() {
    this.ctx = null;
    this.gainNodes = [];
    this.buffers = {};
    this.unlockCtx();
  }

  unlockCtx() {
    if (typeof window === 'undefined') return;
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Unlock on first user gesture
      const unlock = () => {
        if (this.ctx.state === 'suspended') this.ctx.resume();
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('touchstart', unlock);
      };
      window.addEventListener('pointerdown', unlock);
      window.addEventListener('touchstart', unlock);
    }
  }

  async loadBuffer(url) {
    if (this.buffers[url]) return this.buffers[url];
    const res = await fetch(url);
    const arrayBuffer = await res.arrayBuffer();
    const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
    this.buffers[url] = audioBuffer;
    return audioBuffer;
  }

  async play(url, { volume = 1, loop = false } = {}) {
    this.unlockCtx();
    const buffer = await this.loadBuffer(url);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    const gain = this.ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain).connect(this.ctx.destination);
    source.start(0);
    this.gainNodes.push(gain);
    // Clean up after playback
    source.onended = () => {
      const idx = this.gainNodes.indexOf(gain);
      if (idx !== -1) this.gainNodes.splice(idx, 1);
      gain.disconnect();
      source.disconnect();
    };
    return { source, gain };
  }

  pauseAll() {
    this.gainNodes.forEach(gain => gain.gain.value = 0);
  }

  resumeAll() {
    this.gainNodes.forEach(gain => gain.gain.value = 0.75);
  }
}
